import { $, dv, mk } from './dom.js';
import { SKILLS } from './prompts/index.js';
import { ST } from './state.js';

// ── ADMIN ─────────────────────────────────────────────────────────
// Entirely DOM rendering — becomes AdminPage.vue during migration.

/**
 * Render the admin screen — back button, tab bar, and selected tab content
 * (generation prompts, section library, or industry packs).
 */
function renderAdmin() {
  const backBtn = $('admin-back');
  if (backBtn) {
    backBtn.onclick = () => {
      // Only navigate back to screens that have content; fall back to start
      const safe = [
        'start',
        'brief',
        'existing',
        'upload',
        'docview',
        'concept',
        'design',
        'sections',
        'preview',
      ];
      const target = safe.includes(ST.prev) ? ST.prev : 'start';
      // Don't go to content screens if they have no data
      if (['concept', 'design', 'sections', 'preview'].includes(target) && !ST.concept) {
        G.go('start');
      } else {
        G.go(target);
      }
    };
  }
  const tabs = [
    { id: 'generation', label: 'Generation prompts', count: SKILLS.generation.length },
    { id: 'sections', label: 'Section library', count: SKILLS.sections.length },
    { id: 'packs', label: 'Industry packs', count: SKILLS.packs.length },
  ];
  const tb = $('admin-tabs');
  tb.innerHTML = '';
  tabs.forEach(t => {
    const b = mk(
      'button',
      {
        class: `atab${ST.adminTab === t.id ? ' active' : ''}`,
        onclick() {
          ST.adminTab = t.id;
          ST.adminId = null;
          ST.adminEditing = false;
          ST.adminSaved = false;
          renderAdmin();
        },
      },
      t.label,
      mk('span', { class: 'atab-ct' }, String(t.count)),
    );
    tb.append(b);
  });
  const body = $('admin-body');
  body.innerHTML = '';
  if (ST.adminTab === 'generation') {
    body.append(renderAdminGen());
  } else if (ST.adminTab === 'sections') {
    body.append(renderAdminSects());
  } else {
    body.append(renderAdminPacks());
  }
}

function renderAdminGen() {
  const wrap = mk('div', { class: 'agen-layout' });
  const list = mk('div', { class: 'agen-list' });
  SKILLS.generation.forEach(s => {
    const row = mk('div', {
      class: `agen-row${ST.adminId === s.id ? ' active' : ''}`,
      onclick() {
        ST.adminId = s.id;
        ST.adminEditing = false;
        ST.adminSaved = false;
        renderAdmin();
      },
    });
    const nr = mk('div', { class: 'agen-nr' });
    nr.append(
      mk('div', {
        class: 'agen-sdot',
        style: `background:${s.active ? 'var(--teal)' : 'var(--s4)'}`,
      }),
      mk('span', { class: 'agen-name' }, s.name),
    );
    if (!s.active) {
      nr.append(mk('span', { class: 'badge bdg-gray' }, 'off'));
    }
    row.append(nr, mk('div', { class: 'agen-stage' }, s.stage));
    list.append(row);
  });
  wrap.append(list);
  const detail = mk('div', { class: 'agen-detail' });
  const sel = SKILLS.generation.find(s => s.id === ST.adminId);
  if (!sel) {
    const e = mk('div', { class: 'admin-empty' });
    e.append(
      mk('span', { style: 'font-size:28px' }, '⚙'),
      mk('p', {}, 'Select a prompt to view and edit'),
    );
    detail.append(e);
    wrap.append(detail);
    return wrap;
  }
  const hdr = dv(
    'display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:16px;gap:12px',
  );
  const left = dv('flex:1');
  const tr = dv(
    'display:flex;align-items:center;gap:8px;margin-bottom:5px',
    mk('div', { class: 'adetail-title' }, sel.name),
    mk('div', {
      style:
        'width:8px;height:8px;border-radius:50%;background:' +
        (sel.active ? 'var(--teal)' : 'var(--s4)'),
    }),
  );
  const dsc = mk('div', { class: 'adetail-desc' });
  dsc.textContent = sel.stage;
  left.append(tr, dsc);
  const togRow = dv(
    'display:flex;gap:8px;align-items:center;flex-shrink:0',
    mk('span', { style: 'font-size:12px;color:var(--txt2)' }, sel.active ? 'Active' : 'Inactive'),
  );
  const tog = mk('div', {
    class: `tog${sel.active ? ' on' : ''}`,
    onclick() {
      sel.active = !sel.active;
      renderAdmin();
    },
  });
  togRow.append(tog);
  hdr.append(left, togRow);
  detail.append(hdr);
  // Version + Edited (read-only chips)
  const infoChips = mk('div', { class: 'meta-chips', style: 'margin-bottom:12px' });
  [
    ['Version', sel.version],
    ['Edited', sel.edited],
  ].forEach(pair => {
    const l = pair[0],
      v = pair[1];
    const chip = mk('div', { class: 'mc' });
    chip.append(mk('span', { class: 'mc-l' }, l), mk('span', { class: 'mc-v' }, String(v)));
    infoChips.append(chip);
  });
  detail.append(infoChips);

  // Editable settings row: Model, Temp, Tokens
  const MODELS = [
    { value: 'claude-opus-4-6', label: 'Opus 4.6' },
    { value: 'claude-sonnet-4-6', label: 'Sonnet 4.6' },
    { value: 'claude-haiku-4-5', label: 'Haiku 4.5' },
  ];
  const settingsRow = mk('div', { class: 'admin-settings-row' });
  // Model select
  const modelWrap = dv('display:flex;flex-direction:column;gap:4px');
  modelWrap.append(mk('label', { class: 'admin-field-lbl' }, 'Model'));
  const modelSel = mk('select', { class: 'admin-select' });
  MODELS.forEach(m => {
    const opt = mk('option', { value: m.value }, m.label);
    if (sel.model === m.value) {
      opt.selected = true;
    }
    modelSel.append(opt);
  });
  modelSel.onchange = e => {
    sel.model = e.target.value;
    sel.edited = new Date().toISOString().split('T')[0];
    const n = parseFloat(sel.version.replace(/[^0-9.]/g, ''));
    sel.version = `v${(Math.round((n + 0.1) * 10) / 10).toFixed(1)}`;
    renderAdmin();
  };
  modelWrap.append(modelSel);
  settingsRow.append(modelWrap);

  // Temp
  const tempWrap = dv('display:flex;flex-direction:column;gap:4px');
  tempWrap.append(mk('label', { class: 'admin-field-lbl' }, 'Temperature'));
  const tempRow = dv('display:flex;align-items:center;gap:8px');
  const tempSlider = mk('input', {
    type: 'range',
    class: 'admin-range',
    min: '0',
    max: '1',
    step: '0.1',
    value: String(sel.temp),
  });
  const tempVal = mk('span', { class: 'admin-range-val' }, String(sel.temp));
  tempSlider.oninput = e => {
    tempVal.textContent = e.target.value;
  };
  tempSlider.onchange = e => {
    sel.temp = parseFloat(e.target.value);
    sel.edited = new Date().toISOString().split('T')[0];
    const n = parseFloat(sel.version.replace(/[^0-9.]/g, ''));
    sel.version = `v${(Math.round((n + 0.1) * 10) / 10).toFixed(1)}`;
    renderAdmin();
  };
  tempRow.append(tempSlider, tempVal);
  tempWrap.append(tempRow);
  settingsRow.append(tempWrap);

  // Tokens
  const tokWrap = dv('display:flex;flex-direction:column;gap:4px');
  tokWrap.append(mk('label', { class: 'admin-field-lbl' }, 'Max tokens'));
  const tokInp = mk('input', {
    type: 'number',
    class: 'admin-num-inp',
    min: '100',
    max: '32000',
    step: '100',
    value: String(sel.tokens || 1500),
  });
  tokInp.onchange = e => {
    sel.tokens = parseInt(e.target.value, 10) || 1500;
    sel.edited = new Date().toISOString().split('T')[0];
    const n = parseFloat(sel.version.replace(/[^0-9.]/g, ''));
    sel.version = `v${(Math.round((n + 0.1) * 10) / 10).toFixed(1)}`;
    renderAdmin();
  };
  tokWrap.append(tokInp);
  settingsRow.append(tokWrap);

  detail.append(settingsRow);

  // Tags
  const tags = dv('display:flex;gap:5px;flex-wrap:wrap;margin-bottom:16px');
  sel.tags?.forEach(t => {
    tags.append(mk('span', { class: 'badge bdg-gray' }, t));
  });
  detail.append(tags);

  // System prompt
  detail.append(
    mk(
      'div',
      {
        style:
          'font-size:9px;font-weight:700;letter-spacing:.07em;text-transform:uppercase;color:var(--txt3);margin-bottom:8px',
      },
      'System prompt',
    ),
  );
  if (ST.adminEditing) {
    const ta = mk('textarea');
    ta.value = ST.adminDraft || sel.prompt;
    function autoSize() {
      ta.style.height = 'auto';
      ta.style.height = `${ta.scrollHeight}px`;
    }
    ta.oninput = e => {
      ST.adminDraft = e.target.value;
      autoSize();
    };
    requestAnimationFrame(autoSize);
    const sr = mk('div', { class: 'save-row' });
    const save = mk(
      'button',
      {
        class: 'btn btn-p btn-sm',
        onclick() {
          sel.prompt = ST.adminDraft || sel.prompt;
          sel.edited = new Date().toISOString().split('T')[0];
          const n = parseFloat(sel.version.replace(/[^0-9.]/g, ''));
          sel.version = `v${(Math.round((n + 0.1) * 10) / 10).toFixed(1)}`;
          ST.adminEditing = false;
          ST.adminDraft = '';
          ST.adminSaved = true;
          renderAdmin();
          setTimeout(() => {
            ST.adminSaved = false;
            renderAdmin();
          }, 3000);
        },
      },
      'Save changes',
    );
    const cancel = mk(
      'button',
      {
        class: 'btn btn-g btn-sm',
        onclick() {
          ST.adminEditing = false;
          ST.adminDraft = '';
          renderAdmin();
        },
      },
      'Cancel',
    );
    sr.append(save, cancel);
    detail.append(mk('div', { class: 'prompt-editor' }, ta), sr);
  } else {
    const pp = mk('div', { class: 'prompt-prev' });
    const pre = mk('pre');
    pre.textContent = sel.prompt;
    pp.append(pre, mk('div', { class: 'pfade' }));
    const eb = mk(
      'button',
      {
        class: 'btn btn-g btn-sm',
        onclick() {
          ST.adminEditing = true;
          ST.adminDraft = sel.prompt;
          renderAdmin();
        },
      },
      'Edit prompt',
    );
    detail.append(pp, eb);
  }
  if (ST.adminSaved) {
    detail.append(
      mk('div', { class: 'toast-ok' }, 'Saved \u2014 live for all new generation runs'),
    );
  }
  wrap.append(detail);
  return wrap;
}

function renderAdminSects() {
  const w = mk('div', { class: 'acontent' });
  w.append(
    mk('div', { class: 'ac-title' }, 'Section type library'),
    mk(
      'div',
      { class: 'ac-sub' },
      'Controls which section types appear in site plans. Disabled types are excluded globally.',
    ),
  );
  SKILLS.sections.forEach(s => {
    const row = mk('div', { class: `asect-row${s.active ? '' : ' off'}` });
    const t = mk('div', {
      class: `tog${s.active ? ' on' : ''}`,
      onclick() {
        s.active = !s.active;
        renderAdmin();
      },
    });
    const dsc = mk('span', { class: 'asect-desc' });
    dsc.textContent = s.desc;
    row.append(
      t,
      mk('span', { class: 'asect-name' }, s.name),
      dsc,
      mk('span', { class: 'asect-uses' }, s.uses > 0 ? `${s.uses} uses` : 'New'),
    );
    w.append(row);
  });
  w.append(
    mk(
      'div',
      { class: 'admin-note' },
      'Usage counts update from production telemetry. Disabling a widely-used type affects existing site regeneration.',
    ),
  );
  return w;
}

function renderAdminPacks() {
  const w = mk('div', { class: 'acontent' });
  w.append(
    mk('div', { class: 'ac-title' }, 'Industry packs'),
    mk(
      'div',
      { class: 'ac-sub' },
      'Packs overlay base prompts with vertical-specific guidance. Active packs influence section selection and copy emphasis.',
    ),
  );
  SKILLS.packs.forEach(pk => {
    const card = mk('div', { class: `apack-card${pk.active ? '' : ' off'}` });
    const hdr = mk('div', { class: 'apack-hdr' });
    const t = mk('div', {
      class: `tog${pk.active ? ' on' : ''}`,
      onclick() {
        pk.active = !pk.active;
        renderAdmin();
      },
    });
    hdr.append(
      t,
      mk('span', { class: 'apack-name' }, pk.name),
      mk(
        'span',
        { class: `badge ${pk.active ? 'bdg-teal' : 'bdg-gray'}` },
        pk.active ? 'Active' : 'Disabled',
      ),
    );
    const dsc = mk('div', { class: 'apack-desc' });
    dsc.textContent = pk.desc;
    const sects = mk('div', { class: 'apack-sects' });
    pk.sects.forEach(s => {
      sects.append(mk('span', { class: 'apack-sect' }, s));
    });
    card.append(hdr, dsc, sects);
    w.append(card);
  });
  w.append(
    mk(
      'div',
      { class: 'admin-note' },
      'New packs added as JSON config. Pack logic merges with the base concept prompt at generation time.',
    ),
  );
  return w;
}

// ── MODULE EXPORTS ──────────────────────────────────────────────
export { renderAdmin };
